/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CreateDatabasePayload } from '../models/CreateDatabasePayload';
import type { DatabaseRead } from '../models/DatabaseRead';
import type { DatabaseWithRowsAndColumns } from '../models/DatabaseWithRowsAndColumns';
import type { ModifyDatabaseRowsPayload } from '../models/ModifyDatabaseRowsPayload';
import type { ModifyDatabaseSchemaPayload } from '../models/ModifyDatabaseSchemaPayload';
import type { TrackedDatabaseRead } from '../models/TrackedDatabaseRead';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class DatabasesService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * List Databases For Namespace
     * @param namespaceId
     * @returns DatabaseRead Successful Response
     * @throws ApiError
     */
    public listDatabasesForNamespace(
        namespaceId: string,
    ): CancelablePromise<Array<DatabaseRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/databases/list-databases-for-namespace',
            query: {
                'namespace_id': namespaceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Database For Namespace Id
     * @param namespaceId
     * @param requestBody
     * @returns DatabaseRead Successful Response
     * @throws ApiError
     */
    public createDatabaseForNamespaceId(
        namespaceId: string,
        requestBody: CreateDatabasePayload,
    ): CancelablePromise<DatabaseRead> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/databases/create-database-for-namespace-id/{namespace_id}',
            path: {
                'namespace_id': namespaceId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Databases For Entity
     * @param entityId
     * @returns DatabaseRead Successful Response
     * @throws ApiError
     */
    public listDatabasesForEntity(
        entityId: string,
    ): CancelablePromise<Array<DatabaseRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/databases/list-databases-for-entity',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Database For Entity
     * @param entityId
     * @param databaseId
     * @returns DatabaseWithRowsAndColumns Successful Response
     * @throws ApiError
     */
    public getDatabaseForEntity(
        entityId: string,
        databaseId: string,
    ): CancelablePromise<DatabaseWithRowsAndColumns> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/databases/get-database-for-entity/{database_id}',
            path: {
                'entity_id': entityId,
                'database_id': databaseId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Modify Database Rows
     * @param entityId
     * @param databaseId
     * @param requestBody
     * @returns DatabaseWithRowsAndColumns Successful Response
     * @throws ApiError
     */
    public modifyDatabaseRows(
        entityId: string,
        databaseId: string,
        requestBody: ModifyDatabaseRowsPayload,
    ): CancelablePromise<DatabaseWithRowsAndColumns> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/entities/{entity_id}/databases/modify-database-rows/{database_id}',
            path: {
                'entity_id': entityId,
                'database_id': databaseId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Add Tracked Database
     * @param entityId
     * @param databaseId
     * @returns TrackedDatabaseRead Successful Response
     * @throws ApiError
     */
    public addTrackedDatabase(
        entityId: string,
        databaseId: string,
    ): CancelablePromise<TrackedDatabaseRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/databases/add-tracked-database/{database_id}',
            path: {
                'entity_id': entityId,
                'database_id': databaseId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Remove Tracked Database
     * @param entityId
     * @param databaseId
     * @returns any Successful Response
     * @throws ApiError
     */
    public removeTrackedDatabase(
        entityId: string,
        databaseId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/entities/{entity_id}/databases/remove-tracked-database/{database_id}',
            path: {
                'entity_id': entityId,
                'database_id': databaseId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Databases For Cluster
     * @param clusterId
     * @returns DatabaseRead Successful Response
     * @throws ApiError
     */
    public listDatabasesForCluster(
        clusterId: string,
    ): CancelablePromise<Array<DatabaseRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/databases/list-databases-for-cluster',
            path: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Database For Cluster
     * @param clusterId
     * @param databaseId
     * @returns DatabaseWithRowsAndColumns Successful Response
     * @throws ApiError
     */
    public getDatabaseForCluster(
        clusterId: string,
        databaseId: string,
    ): CancelablePromise<DatabaseWithRowsAndColumns> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/databases/get-database-for-cluster/{database_id}',
            path: {
                'cluster_id': clusterId,
                'database_id': databaseId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Modify Database Schema
     * @param clusterId
     * @param databaseId
     * @param requestBody
     * @returns DatabaseWithRowsAndColumns Successful Response
     * @throws ApiError
     */
    public modifyDatabaseSchema(
        clusterId: string,
        databaseId: string,
        requestBody: ModifyDatabaseSchemaPayload,
    ): CancelablePromise<DatabaseWithRowsAndColumns> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/clusters/{cluster_id}/databases/modify-database-schema/{database_id}',
            path: {
                'cluster_id': clusterId,
                'database_id': databaseId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Rename Database
     * @param clusterId
     * @param databaseId
     * @param newName
     * @returns any Successful Response
     * @throws ApiError
     */
    public renameDatabase(
        clusterId: string,
        databaseId: string,
        newName: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/clusters/{cluster_id}/databases/rename-database/{database_id}',
            path: {
                'cluster_id': clusterId,
                'database_id': databaseId,
            },
            query: {
                'new_name': newName,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Database
     * @param clusterId
     * @param databaseId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteDatabase(
        clusterId: string,
        databaseId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/clusters/{cluster_id}/databases/delete-database/{database_id}',
            path: {
                'cluster_id': clusterId,
                'database_id': databaseId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

}
